/**
 * جدول العيادات — the clinics' day sheet, planned and printed.
 *
 * Every morning the clinics desk used to type this table into Word: each
 * doctor in session, their specialty, when they start, which secretary sits
 * with them, where, and who sent their apologies — then print it for the
 * front desk and the hall. Everything on it is now in the system, so the
 * sheet builds itself (Backend/lib/clinicRoster.js) and prints with one
 * button, in colour, on one A4 page.
 *
 * What the desk still decides, it decides here:
 *
 *   the secretary   a small pool of clinic secretaries is handed to different
 *                   doctors each day. The choice made here is the reach that
 *                   secretary's screens follow today (lib/rosterScope.js).
 *   the apology     a doctor who is not coming is marked "معتذر" once the desk
 *                   has confirmed it; the day closes to new bookings, and the
 *                   desk can cancel the day's bookings and ring the patients.
 *   start & place   a late start or another room, for this day only.
 */

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  DateInput,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
  TimeInput,
} from '../../components/common';
import { StatTile3D } from '../../components/common/Tile3D';
import { operationsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { arabicDayOf, formatDateArabic, todayKey } from '../../domain/dates';
import { formatDmy, ltr } from '../../domain/bidi';
import { logoPlateClass } from '../../domain/logoBackdrop';
import { SHEET_STATUS, previousDay, sheetTimeLabel, startMoved, toneForText } from '../../domain/daySheet';
import { printArea } from '../hospitalShared/labels';

const pad = (n) => String(n).padStart(2, '0');
const stamp = (d = new Date()) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

/* ========================================================================== */
/* The paper                                                                  */
/* ========================================================================== */

/** The printed sheet: the hospital's header, one row per clinic, the day's totals. */
export const DaySheetPaper = forwardRef(function DaySheetPaper({ date, rows, summary }, ref) {
  const { institutionName, institutionLogo, institutionLogoBackdrop } = usePermissions();
  return (
    <div ref={ref} className="print-sheet cds-paper" dir="rtl">
      <header className="cds-paper__head">
        <div className="cds-paper__brand">
          {institutionLogo ? (
            <img src={institutionLogo} alt="" className={`cds-paper__logo ${logoPlateClass(institutionLogoBackdrop)}`} />
          ) : (
            <span className="cds-paper__logo cds-paper__logo--mark" aria-hidden="true">
              <Icon name="hospital" size={26} />
            </span>
          )}
          <div className="min-w-0">
            <p className="cds-paper__org">{institutionName || 'المنشأة الطبية'}</p>
            <h1 className="cds-paper__title">
              <Icon name="calendar-days" size={22} /> جدول العيادات
            </h1>
            <p className="cds-paper__date">
              {arabicDayOf(date)} {formatDateArabic(date)} <span dir="ltr">({ltr(date.split('-').reverse().join('/'))})</span>
            </p>
          </div>
        </div>
        <div className="cds-paper__chips">
          <span className="cds-chip cds-chip--cyan">
            <Icon name="building-2" size={13} /> {summary.clinics} عيادة
          </span>
          <span className="cds-chip cds-chip--violet">
            <Icon name="stethoscope" size={13} /> {summary.doctors} طبيب
          </span>
          <span className="cds-chip cds-chip--emerald">
            <Icon name="calendar-check" size={13} /> {summary.bookings} حجز
          </span>
          {summary.excused > 0 && (
            <span className="cds-chip cds-chip--red">
              <Icon name="user-x" size={13} /> {summary.excused} معتذر
            </span>
          )}
        </div>
      </header>

      <table className="cds-table">
        <thead>
          <tr>
            <th style={{ width: '5%' }}>م</th>
            <th style={{ width: '22%' }}>
              <Icon name="stethoscope" size={13} /> الطبيب
            </th>
            <th style={{ width: '17%' }}>
              <Icon name="activity" size={13} /> التخصص
            </th>
            <th style={{ width: '13%' }}>
              <Icon name="clock" size={13} /> بدء الكشف
            </th>
            <th style={{ width: '17%' }}>
              <Icon name="user-round" size={13} /> السكرتير
            </th>
            <th style={{ width: '16%' }}>
              <Icon name="map-pin" size={13} /> المكان
            </th>
            <th style={{ width: '10%' }}>
              <Icon name="users" size={13} /> الحجوزات
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const tone = toneForText(row.specialty);
            const off = row.status !== 'scheduled';
            return (
              <tr key={row.clinicId} className={off ? `cds-row cds-row--${row.status}` : 'cds-row'}>
                <td>
                  <span className="cds-index">{i + 1}</span>
                </td>
                <td>
                  <div className="cds-doctor">
                    <span className="cds-dot" style={{ background: row.clinicColor || '#06b6d4' }} />
                    <div className="min-w-0">
                      <b>{row.doctorName ? `د. ${row.doctorName.replace(/^د\.\s*/, '')}` : '—'}</b>
                      {row.clinicName && <span className="cds-sub">{row.clinicName}</span>}
                    </div>
                  </div>
                </td>
                <td>{row.specialty ? <span className={`cds-chip cds-chip--${tone}`}>{row.specialty}</span> : '—'}</td>
                <td>
                  {off ? (
                    <span className={`cds-chip cds-chip--${row.status === 'excused' ? 'red' : 'slate'}`}>
                      <Icon name={SHEET_STATUS[row.status].icon} size={13} /> {sheetTimeLabel(row)}
                    </span>
                  ) : (
                    <span className={`cds-time ${startMoved(row) ? 'cds-time--moved' : ''}`}>{ltr(sheetTimeLabel(row))}</span>
                  )}
                </td>
                <td>{off ? '—' : row.secretary?.name || <span className="cds-missing">لم يُحدَّد</span>}</td>
                <td>{row.location || '—'}</td>
                <td>{off ? '—' : <span className="cds-count">{row.bookings}</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <footer className="cds-paper__foot">
        <span>
          <Icon name="info" size={12} /> الموعد هو بدء الكشف. الصفوف الحمراء: الطبيب معتذر عن هذا اليوم.
        </span>
        <span dir="ltr">{ltr(stamp())}</span>
      </footer>
    </div>
  );
});

/* ========================================================================== */
/* The apology                                                                */
/* ========================================================================== */

function ExcuseDialog({ row, date, onClose, onDone }) {
  const { success, error: toastError } = useNotifications();
  const [reason, setReason] = useState('');
  const [cancelBookings, setCancelBookings] = useState(row.bookings > 0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const outcome = await operationsApi.excuseDoctor({ doctorId: row.doctorId, date, reason: reason.trim() || null, cancelBookings });
      success(`تم تسجيل اعتذار ${row.doctorName || 'الطبيب'} ليوم ${formatDmy(date)}`);
      onDone();
      if (outcome.cancelled?.length) setResult(outcome);
      else onClose();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <Modal open onClose={onClose} title="المرضى المطلوب إبلاغهم" icon="phone-call" size="lg">
        <p className="mb-3 text-sm text-slate-300">أُلغيت {result.cancelled.length} حجوزات. اتصل بكل مريض أو أرسل له رسالة واتساب جاهزة:</p>
        <ul className="space-y-2">
          {result.cancelled.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
              <Icon name="user" size={15} className="text-cyan-300" />
              <span className="min-w-0 flex-1 truncate font-bold text-white">{b.patientName || '—'}</span>
              <span className="text-xs text-slate-400" dir="ltr">
                {b.time}
              </span>
              {b.telUrl && (
                <a className="btn-ghost !min-h-[36px] !px-3" href={b.telUrl}>
                  <Icon name="phone" size={14} /> اتصال
                </a>
              )}
              {b.waUrl && (
                <a className="btn-ghost !min-h-[36px] !px-3" href={b.waUrl} target="_blank" rel="noreferrer noopener">
                  <Icon name="message-circle" size={14} /> واتساب
                </a>
              )}
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`اعتذار ${row.doctorName ? `د. ${row.doctorName.replace(/^د\.\s*/, '')}` : 'الطبيب'}`}
      icon="user-x"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            رجوع
          </Button>
          <Button variant="danger" icon="user-x" loading={busy} onClick={submit}>
            تأكيد الاعتذار
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="excuse-strip flex items-start gap-3 rounded-xl p-3 text-sm">
          <Icon name="alert-triangle" size={18} className="mt-0.5 shrink-0" />
          <p>
            يظهر الطبيب <b>معتذراً</b> في جدول يوم {ltr(formatDmy(date))} ويُغلق اليوم أمام الحجز الجديد. تأكد من الاعتذار مع الطبيب قبل التسجيل.
          </p>
        </div>
        <Field label="سبب الاعتذار (اختياري)">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="ظرف طارئ، سفر، مؤتمر…" />
        </Field>
        {row.bookings > 0 && (
          <Checkbox
            label={`إلغاء حجوزات اليوم (${row.bookings}) وعرض المرضى للاتصال بهم`}
            checked={cancelBookings}
            onChange={(e) => setCancelBookings(e.target.checked)}
          />
        )}
      </div>
    </Modal>
  );
}

/* ========================================================================== */
/* The planner                                                                */
/* ========================================================================== */

/** One clinic's row on screen: the same columns as the paper, editable. */
/** "(مع د. هالة 10:00 ص)" — where else this secretary is today, in the option's text. */
function busyLabel(elsewhere, clinicId) {
  const others = (elsewhere || []).filter((r) => String(r.clinicId) !== String(clinicId));
  if (others.length === 0) return '';
  const first = others[0];
  const who = first.doctorName ? `د. ${first.doctorName.replace(/^د\.\s*/, '')}` : first.clinicName;
  return ` (مع ${who} ${sheetTimeLabel(first)}${others.length > 1 ? ` +${others.length - 1}` : ''})`;
}

function PlannerRow({ row, index, date, secretaries, mayPlan, mayExcuse, busySecretaries, onSaved, onExcuse, onUnexcuse }) {
  const { error: toastError } = useNotifications();
  const [location, setLocation] = useState(row.location || '');
  const [saving, setSaving] = useState(false);
  const off = row.status !== 'scheduled';
  const status = SHEET_STATUS[row.status] || SHEET_STATUS.scheduled;

  useEffect(() => setLocation(row.location || ''), [row.location]);

  const save = async (patch) => {
    setSaving(true);
    try {
      await operationsApi.planClinicDay(row.clinicId, { date, ...patch });
      onSaved();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className={`cds-plan-row ${off ? `cds-plan-row--${row.status}` : ''}`}>
      <td className="cds-plan-index">{index + 1}</td>
      <td>
        <div className="flex min-w-0 items-center gap-2">
          <span className="cds-plan-dot" style={{ '--dot': row.clinicColor || '#22d3ee' }} />
          <div className="min-w-0">
            <p className="truncate font-bold text-white">{row.doctorName ? `د. ${row.doctorName.replace(/^د\.\s*/, '')}` : '—'}</p>
            <p className="truncate text-[11px] text-slate-400">{row.clinicName}</p>
          </div>
        </div>
      </td>
      <td>
        {row.specialty ? (
          <span className={`cds-plan-chip cds-plan-chip--${toneForText(row.specialty)}`}>{row.specialty}</span>
        ) : (
          <span className="text-slate-500">—</span>
        )}
      </td>
      <td>
        {off ? (
          <Badge className={`cds-plan-status cds-plan-status--${row.status}`} icon={status.icon}>
            {status.label}
          </Badge>
        ) : mayPlan ? (
          <div className="w-[9.5rem]">
            <TimeInput
              value={row.startTime || ''}
              onChange={(e) => e.target.value && e.target.value !== row.startTime && save({ startTime: e.target.value })}
              disabled={saving}
            />
          </div>
        ) : (
          <span className="font-bold text-white" dir="ltr">
            {sheetTimeLabel(row)}
          </span>
        )}
      </td>
      <td>
        {off ? (
          <span className="text-slate-500">—</span>
        ) : mayPlan ? (
          <Select
            value={row.secretary?.id ? String(row.secretary.id) : ''}
            onChange={(e) => save({ secretaryUserId: e.target.value || null })}
            disabled={saving}
            aria-label={`سكرتير ${row.doctorName || ''}`}
            className={row.secretary ? '' : 'cds-plan-select--empty'}
          >
            <option value="">— اختر سكرتيراً —</option>
            {secretaries.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {busyLabel(busySecretaries.get(String(s.id)), row.clinicId)}
              </option>
            ))}
          </Select>
        ) : (
          <span className="text-slate-200">{row.secretary?.name || '—'}</span>
        )}
      </td>
      <td>
        {mayPlan ? (
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onBlur={() => location !== (row.location || '') && save({ location })}
            placeholder="المبنى / الدور / الغرفة"
            disabled={saving}
            aria-label={`مكان عيادة ${row.doctorName || ''}`}
          />
        ) : (
          <span className="text-slate-200">{row.location || '—'}</span>
        )}
      </td>
      <td className="text-center">
        <span className="cds-plan-count">{row.bookings}</span>
      </td>
      {mayExcuse && (
        <td>
          {row.status === 'excused' ? (
            <Button variant="ghost" icon="rotate-ccw" onClick={() => onUnexcuse(row)}>
              إلغاء الاعتذار
            </Button>
          ) : row.status === 'scheduled' ? (
            <Button variant="subtle" icon="user-x" onClick={() => onExcuse(row)}>
              اعتذار
            </Button>
          ) : null}
        </td>
      )}
    </tr>
  );
}

/**
 * The whole tab: date, totals, the planner, and the print.
 * `autoPrint` opens the preview and the print dialog together — the
 * one-button print the desk asked for.
 */
export function ClinicDaySheet({ initialDate = todayKey() }) {
  const { success, error: toastError } = useNotifications();
  const [date, setDate] = useState(initialDate);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [excusing, setExcusing] = useState(null);
  const [preview, setPreview] = useState(false);
  const [copying, setCopying] = useState(false);
  const paper = useRef(null);

  const load = useCallback(async () => {
    try {
      const sheet = await operationsApi.daySchedule(date);
      setData(sheet);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, [date]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  // One click: open the preview and print it as soon as it has rendered.
  useEffect(() => {
    if (preview !== 'print' || !paper.current) return undefined;
    const timer = setTimeout(() => {
      printArea(paper.current, { title: `جدول العيادات ${formatDmy(date).replace(/\//g, '-')}` });
      setPreview(true);
    }, 250);
    return () => clearTimeout(timer);
  }, [preview, date]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const summary = data?.summary || { clinics: 0, scheduled: 0, excused: 0, closed: 0, withoutSecretary: 0, bookings: 0, doctors: 0 };

  // Which clinic each secretary already sits with today, so the menu says so.
  const busySecretaries = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      if (!row.secretary || row.status !== 'scheduled') continue;
      const key = String(row.secretary.id);
      map.set(key, [...(map.get(key) || []), row]);
    }
    return map;
  }, [rows]);

  const unexcuse = async (row) => {
    try {
      await operationsApi.unexcuseDoctor(row.doctorId, date);
      success('أُلغي الاعتذار — الطبيب في الجدول من جديد');
      load();
    } catch (err) {
      toastError(err.message);
    }
  };

  const copyYesterday = async () => {
    setCopying(true);
    try {
      const { copied } = await operationsApi.copyDayPlan(previousDay(date), date);
      success(copied ? `نُسخ توزيع ${copied} عيادة من اليوم السابق` : 'لا يوجد توزيع في اليوم السابق لنسخه');
      load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="cds-toolbar glass-panel flex flex-wrap items-center gap-3 rounded-2xl p-4">
        <span className="tile3d__face tone-teal !h-11 !w-11 shrink-0" aria-hidden="true">
          <Icon name="calendar-days" size={20} className="tile3d__icon" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold text-white">جدول العيادات اليومي</p>
          <p className="text-xs text-slate-400">
            {arabicDayOf(date)} {formatDateArabic(date)} — وزّع السكرتارية، سجّل الاعتذارات، واطبع الجدول بضغطة واحدة.
          </p>
        </div>
        <DateInput value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-auto" />
        {data?.mayPlan && (
          <Button variant="ghost" icon="copy" loading={copying} onClick={copyYesterday}>
            نسخ توزيع أمس
          </Button>
        )}
        <Button variant="ghost" icon="refresh-cw" onClick={load}>
          تحديث
        </Button>
        <Button icon="printer" onClick={() => setPreview('print')} disabled={!rows.length}>
          طباعة الجدول
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile3D icon="building-2" label="عيادات اليوم" value={data ? summary.clinics : '—'} tone="cyan" delay={0} />
        <StatTile3D icon="stethoscope" label="أطباء في الموعد" value={data ? summary.doctors : '—'} tone="emerald" delay={1} />
        <StatTile3D icon="user-x" label="معتذرون" value={data ? summary.excused : '—'} tone="rose" delay={2} />
        <StatTile3D icon="user-round" label="بلا سكرتير" value={data ? summary.withoutSecretary : '—'} tone="amber" delay={3} />
        <StatTile3D icon="calendar-check" label="حجوزات اليوم" value={data ? summary.bookings : '—'} tone="purple" delay={4} />
      </div>

      {error ? (
        <ErrorState error={error} onRetry={load} />
      ) : !data ? (
        <Spinner label="جارٍ تجهيز جدول العيادات" variant="inline" />
      ) : rows.length === 0 ? (
        <EmptyState icon="calendar-x" title="لا عيادات في هذا اليوم" message="لا توجد عيادة لأطباء المنشأة تعمل في هذا اليوم حسب أيام العمل المسجلة." />
      ) : (
        <div className="cds-plan glass-panel custom-scrollbar overflow-x-auto rounded-2xl">
          <table className="cds-plan-table w-full min-w-[64rem] text-sm">
            <thead>
              <tr>
                <th>م</th>
                <th>الطبيب والعيادة</th>
                <th>التخصص</th>
                <th>بدء الكشف</th>
                <th>السكرتير اليوم</th>
                <th>المكان</th>
                <th className="text-center">الحجوزات</th>
                {data.mayExcuse && <th>الحضور</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <PlannerRow
                  key={row.clinicId}
                  row={row}
                  index={i}
                  date={date}
                  secretaries={data.secretaries || []}
                  mayPlan={data.mayPlan}
                  mayExcuse={data.mayExcuse}
                  busySecretaries={busySecretaries}
                  onSaved={load}
                  onExcuse={setExcusing}
                  onUnexcuse={unexcuse}
                />
              ))}
            </tbody>
          </table>
          {data.mayPlan && (data.secretaries || []).length === 0 && (
            <p className="flex items-center gap-2 border-t border-white/5 px-4 py-3 text-xs text-amber-200">
              <Icon name="info" size={14} /> لا توجد حسابات سكرتارية في المنشأة بعد — يضيفها مدير المنشأة من فريق المستشفى.
            </p>
          )}
        </div>
      )}

      {excusing && <ExcuseDialog row={excusing} date={date} onClose={() => setExcusing(null)} onDone={load} />}

      <Modal
        open={Boolean(preview)}
        onClose={() => setPreview(false)}
        title="معاينة جدول العيادات"
        icon="printer"
        size="xl"
        footer={
          <div className="flex justify-end">
            <Button icon="printer" onClick={() => printArea(paper.current, { title: `جدول العيادات ${formatDmy(date).replace(/\//g, '-')}` })}>
              طباعة / حفظ PDF
            </Button>
          </div>
        }
      >
        <DaySheetPaper ref={paper} date={date} rows={rows} summary={summary} />
      </Modal>
    </div>
  );
}

export default ClinicDaySheet;
